import React from "react";
import CommonModal from "../../components/modals/CommonModal";
import {
  Typography,
  Stack,
  Divider,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { TariffV2, TariffSlabV2 } from "../../utils/tariffServiceV2";
import { formatDate } from "../../utils/helper";
import { Calendar, Layers, Tag, Clock, FileText } from "lucide-react";

interface TariffDetailsModalProps {
  open: boolean;
  onClose: () => void;
  tariff: TariffV2 | null;
}

const LabelValue = ({ label, value, icon: Icon, color = "inherit" }: any) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    {Icon && (
      <Box sx={{ color: "#64748B", display: "flex", p: 0.5 }}>
        <Icon size={16} />
      </Box>
    )}
    <Box>
      <Typography
        variant="caption"
        color="#64748B"
        sx={{ fontWeight: 600, fontSize: "10px", textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: color === "inherit" ? "#1E293B" : color }}
      >
        {value}
      </Typography>
    </Box>
  </Stack>
);

const formatTime = (time: any) => {
  if (!time) return "-";
  if (typeof time === "string") {
    if (!time.includes(":")) return time || "-";
    // Handle string format "HH:mm:ss" or similar
    return time.split(":").slice(0, 2).join(":");
  }
  const h = String(time.hour ?? 0).padStart(2, "0");
  const m = String(time.minute ?? 0).padStart(2, "0");
  return `${h}:${m}`;
};

const TariffDetailsModal: React.FC<TariffDetailsModalProps> = ({
  open,
  onClose,
  tariff,
}) => {
  if (!tariff) return null;

  const body = (
    <Box
      sx={{ width: "100%", pt: 2, display: "flex", flexDirection: "column" }}
    >
      {/* Metadata Section - Manual Padding */}
      <Box sx={{ px: 3, width: "100%", boxSizing: "border-box" }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <LabelValue
              label="Tariff Code"
              value={tariff.tariffCode || "-"}
              icon={Tag}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <LabelValue
              label="Tariff Name"
              value={tariff.name || "-"}
              color="#1064ED"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <LabelValue
              label="Status"
              value={
                <Chip
                  label={tariff.isActive ? "Active" : "Inactive"}
                  size="small"
                  color={tariff.isActive ? "success" : "default"}
                  sx={{ height: "18px", fontSize: "10px", fontWeight: 700 }}
                />
              }
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <LabelValue
              label="Effective From"
              value={
                tariff.effectiveFrom
                  ? formatDate(tariff.effectiveFrom, "DD MMM YYYY")
                  : "-"
              }
              icon={Calendar}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelValue
              label="Effective To"
              value={
                tariff.effectiveTo
                  ? formatDate(tariff.effectiveTo, "DD MMM YYYY")
                  : "-"
              }
              icon={Calendar}
              color="#F43F5E"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <LabelValue
              label="Description"
              value={tariff.description || "No description provided."}
              icon={FileText}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2, borderColor: "#464A4F26" }} />

        {/* Section Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{ color: "#1064ED", display: "flex" }}>
            <Layers size={18} />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "#1E293B" }}
          >
            TARIFF SLABS
          </Typography>
        </Stack>
      </Box>

      {/* Slabs Table - Edge to Edge */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 0,
          borderLeft: "none",
          borderRight: "none",
          borderTop: "1px solid #464A4F26",
          borderBottom: "1px solid #464A4F26",
          width: "100%",
          backgroundColor: "#fff",
        }}
      >
        <Table sx={{ width: "100%" }}>
          <TableHead sx={{ backgroundColor: "rgba(70, 74, 79, 0.04)" }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                  pl: 3,
                }}
              >
                SLAB NAME
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                }}
                align="center"
              >
                SLAB TYPE
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                }}
                align="right"
              >
                MIN UNITS
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                }}
                align="right"
              >
                MAX UNITS
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                }}
                align="right"
              >
                RATE
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                }}
                align="right"
              >
                FIXED
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                }}
                align="center"
              >
                TOU
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "11px",
                  py: 1.5,
                  pr: 3,
                }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tariff.slabs && tariff.slabs.length > 0 ? (
              tariff.slabs.map((slab: TariffSlabV2) => (
                <TableRow
                  key={slab.id}
                  sx={{
                    "&:hover": { backgroundColor: "rgba(16, 100, 237, 0.04)" },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#1E293B",
                      fontSize: "11px",
                      pl: 3,
                    }}
                  >
                    {slab.slabName}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "11px" }}>
                    <Chip
                      label={slab.slabType || "-"}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        fontSize: "9px",
                        height: "18px",
                        color: "#64748B",
                        borderColor: "#E2E8F0",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "11px" }}>
                    {slab.minUnits ?? "-"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "11px" }}>
                    {slab.maxUnits ?? "-"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, color: "#1064ED", fontSize: "11px" }}
                  >
                    ₹{slab.ratePerUnit ?? "-"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "11px" }}>
                    ₹{slab.fixedCharge ?? "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Clock size={10} color="#64748B" />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "#475569",
                          fontSize: "10px",
                        }}
                      >
                        {slab.timeOfUseStart || slab.timeOfUseEnd
                          ? `${formatTime(slab.timeOfUseStart)} - ${formatTime(slab.timeOfUseEnd)}`
                          : "-"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ pr: 3 }}>
                    <Chip
                      label={slab.isActive ? "Active" : "Inactive"}
                      size="small"
                      variant="outlined"
                      color={slab.isActive ? "success" : "default"}
                      sx={{ fontWeight: 700, fontSize: "8px", height: "16px" }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#64748B",
                      fontStyle: "italic",
                      fontSize: "12px",
                    }}
                  >
                    No slabs available.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <CommonModal
      title="Tariff Details"
      open={open}
      onClose={onClose}
      body={body}
      maxWidth="sm"
      fullWidth
      dialogContentSx={{ p: 0, overflowX: "hidden" }}
    />
  );
};

export default TariffDetailsModal;
